import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Stack,
  MenuItem,
  Select,
  InputLabel,
  FormControl
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { format, isValid } from 'date-fns';
import { BlockedTime, TeamMember } from '../../types';
import { blockedTimesApi } from '../../firebase/services';
import TranslationTabs, { TranslationValues } from './TranslationTabs';

interface BlockedTimeManagementProps {
  blockedTimes: BlockedTime[];
  teamMembers?: TeamMember[];
  onRefresh: () => void;
}

const BlockedTimeManagement: React.FC<BlockedTimeManagementProps> = ({
  blockedTimes,
  teamMembers = [],
  onRefresh
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBlockedTime, setSelectedBlockedTime] = useState<BlockedTime | null>(null);
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [startTime, setStartTime] = useState<Date | null>(new Date());
  const [endTime, setEndTime] = useState<Date | null>(new Date());
  const [reason, setReason] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [translations, setTranslations] = useState<TranslationValues>({ fr: {}, es: {} });

  const handleOpenDialog = () => {
    setSelectedBlockedTime(null);
    setSelectedDate(new Date());
    setStartTime(new Date());
    setEndTime(new Date());
    setReason('');
    setSelectedMemberId('');
    setTranslations({ fr: {}, es: {} });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setError(null);
  };

  const handleOpenDeleteDialog = (blockedTime: BlockedTime) => {
    setSelectedBlockedTime(blockedTime);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReason(e.target.value);
  };

  const handleSubmit = async () => {
    setError(null);
    
    if (!selectedDate || !startTime || !endTime) {
      setError('Please select date and time');
      return;
    }

    if (!isValid(startTime) || !isValid(endTime)) {
      setError('Invalid time selection');
      return;
    }

    if (startTime >= endTime) {
      setError('End time must be after start time');
      return;
    }

    setIsLoading(true);

    try {
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      const formattedStartTime = format(startTime, 'HH:mm');
      const formattedEndTime = format(endTime, 'HH:mm');

      const newBlockedTime: Omit<BlockedTime, 'id'> = {
        date: formattedDate,
        startTime: formattedStartTime,
        endTime: formattedEndTime,
        reason: reason || 'Bloqué par admin',
        ...(selectedMemberId ? { teamMemberId: selectedMemberId } : {}),
        translations,
      };

      await blockedTimesApi.create(newBlockedTime);
      onRefresh();
      handleCloseDialog();
    } catch (error) {
      console.error('Error adding blocked time:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedBlockedTime) return;

    setIsLoading(true);
    try {
      await blockedTimesApi.delete(selectedBlockedTime.id);
      onRefresh();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Error deleting blocked time:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getMemberName = (memberId?: string) => {
    if (!memberId) return 'Tous';
    const m = teamMembers.find(t => t.id === memberId);
    return m ? m.name : '—';
  };

  // Get paginated blocked times
  const paginatedBlockedTimes = blockedTimes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);


  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2">
          Créneaux bloqués
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Bloquer un créneau
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Début</TableCell>
              <TableCell>Fin</TableCell>
              <TableCell>Coiffeur</TableCell>
              <TableCell>Raison</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedBlockedTimes.map((blockedTime) => (
              <TableRow key={blockedTime.id}>
                <TableCell>{format(new Date(blockedTime.date + 'T00:00:00'), 'dd/MM/yyyy')}</TableCell>
                <TableCell>{blockedTime.startTime}</TableCell>
                <TableCell>{blockedTime.endTime}</TableCell>
                <TableCell>{getMemberName(blockedTime.teamMemberId)}</TableCell>
                <TableCell>{blockedTime.reason}</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDeleteDialog(blockedTime)}
                    title="Supprimer"
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {blockedTimes.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Aucun créneau bloqué
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={blockedTimes.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Lignes par page :"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
      />

      {/* Add Blocked Time Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Bloquer un créneau</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            {teamMembers.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Coiffeur (optionnel)</InputLabel>
                <Select
                  value={selectedMemberId}
                  label="Coiffeur (optionnel)"
                  onChange={(e) => setSelectedMemberId(e.target.value)}
                >
                  <MenuItem value="">Tous les coiffeurs</MenuItem>
                  {teamMembers.filter(m => m.isActive).map(m => (
                    <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Box>
              <DatePicker
                label="Date"
                value={selectedDate}
                onChange={(newValue) => setSelectedDate(newValue)}
                disablePast
                slotProps={{
                  textField: {
                    fullWidth: true
                  }
                }}
              />
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                <TimePicker
                  label="Start Time"
                  value={startTime}
                  onChange={(newValue) => setStartTime(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </Box>
              <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                <TimePicker
                  label="End Time"
                  value={endTime}
                  onChange={(newValue) => setEndTime(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </Box>
            </Stack>
            <Box>
              <TextField
                label="Raison (optionnel)"
                value={reason}
                onChange={handleReasonChange}
                fullWidth
                placeholder="ex: Réunion, Congé, Fermé..."
              />
            </Box>
            <Box>
              <TranslationTabs
                fields={[{ key: 'reason', label: 'Raison' }]}
                values={translations}
                onChange={setTranslations}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isLoading}>
            Annuler
          </Button>
          <Box sx={{ position: 'relative' }}>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={isLoading}
            >
              {isLoading ? 'Enregistrement...' : 'Bloquer'}
            </Button>
            {isLoading && (
              <CircularProgress
                size={20}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-10px',
                  marginLeft: '-10px',
                }}
              />
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Supprimer le blocage</DialogTitle>
        <DialogContent>
          <Typography>
            Confirmer la suppression ? Ce créneau redeviendra disponible à la réservation.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={isLoading}>
            Annuler
          </Button>
          <Box sx={{ position: 'relative' }}>
            <Button
              onClick={handleDelete}
              variant="contained"
              color="error"
              disabled={isLoading}
            >
              {isLoading ? 'Suppression...' : 'Supprimer'}
            </Button>
            {isLoading && (
              <CircularProgress
                size={20}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-10px',
                  marginLeft: '-10px',
                }}
              />
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BlockedTimeManagement;
